import { useDispatch, useSelector } from "react-redux";

import ProductImage from "~/domains/product/components/ProductImage";
import { Box, Button, IconButton, Stack, styled } from "~/common/mui";
import { SimpleStockIndicator } from "~/domains/product/components/StockIndicator/StockIndicator";
import { useAddProductToWishlistFlow } from "~/features/wishlist/hooks/useAddProductToWishlistFlow";
import { type RootState } from "~/app/store";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { removeFromComparison } from "~/features/compareProducts/redux/actions";
import { OldDesignationParameter } from "~/domains/product/components/ProductParams";
import { userIsRegistered } from "~/domains/user/utils/roles";
import {
  getListPrice,
  getTariff,
  isProductPMA,
  resolveSmallestPackageFromProductData,
} from "~/domains/product/utils/productDescriptorUtils";
import { NewVersionMessage } from "~/common/components/Messages/NewVersionMessage";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { AddToCartService } from "~/features/addToCart/AddToCartService";
import { canSeeAddToCartButton, canSeeListPrice } from "~/utils/features";
import { useTranslation } from "react-i18next";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import { calculateSalesCampaign4PDP, useSelectPricingDetails } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { SideButton } from "~/common/components/Button/SideButton";
import { selectProductIsInStock } from "~/utils/pna/availabilitySelectors";
import { isTariffSurchargeEnabled } from "~/features/cart/cart/utils/cartUtils";
import { useMemo } from "react";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import { Product } from "~/domains/product/Product";
import { PriceElements, type PriceProps } from "~/domains/pricing/components/Price/PriceV4";
import { SaleDiscountIndicator } from "~/domains/salesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";
import InfoIcon from "@mui/icons-material/Info";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import CloseIcon from "@mui/icons-material/Close";

const WIDTHS = {
  product: "120px",
  gap: "10px",
  brand: "32px",
  manufacturer: "32px",
};

interface Props {
  productCode: string;
}

export const usePCCardLogic = (productCode: string) => {
  const priceReference = getInitialPriceReference();
  const productDescriptor = useSelector((state: RootState) => {
    return selectProductDescriptor(state, {
      productCode,
      priceReference,
    });
  });

  const packCode = resolveSmallestPackageFromProductData(productDescriptor);
  const { data } = useMemo(() => calculateSalesCampaign4PDP({ productDescriptor }), [productDescriptor]);
  const salesData = data?.salePerPack?.[packCode];
  const { price, oldPrice, netPrice, discountFormatted } = useSelectPricingDetails({
    packCode,
    quantity: 1,
    priceReference,
    productCode,
    salesData,
    saleActivated: true,
  });
  const hasSurcharge = (netPrice?.surcharge ?? 0) > 0;

  const showListPrice = canSeeListPrice({
    product: productDescriptor,
    isSpecialPrice: netPrice?.isSpecial ?? false,
    hasSurcharge,
  });

  const listPrice = showListPrice ? getListPrice({ product: productDescriptor, packCode }) : null;

  const tariffSurcharge = isTariffSurchargeEnabled() && getTariff(productDescriptor);

  return {
    price,
    oldPrice,
    netPrice,
    discountFormatted,
    productDescriptor,
    showListPrice,
    listPrice,
    packCode,
    tariffSurcharge,
    hasSurcharge,
  };
};

const ProductComparisonCard = ({ productCode }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    price,
    oldPrice,
    netPrice,
    discountFormatted,
    productDescriptor,
    packCode,
    showListPrice,
    listPrice,
    tariffSurcharge,
    hasSurcharge,
  } = usePCCardLogic(productCode);

  const wishlistButtonVisible = userHasPermission(Permission.ADD_TO_WISHLIST);
  const atcButtonVisible = canSeeAddToCartButton();

  const handleCloseClick = () => {
    dispatch(removeFromComparison(productCode, productDescriptor.base.designation));
  };

  const doAddToCart = () => {
    AddToCartService.openAddToCartWizard(
      {
        productCode: productDescriptor.base.code,
        restrictionQuery: "",
      },
      {
        page: "COMPARISON",
      },
    );
  };

  const [openAddToWishlist, addToWishlistModal] = useAddProductToWishlistFlow({ productInput: productDescriptor.base });
  const onWishlistBtnClick = () => {
    openAddToWishlist();
  };

  const isInStock = selectProductIsInStock(productDescriptor.availability);

  const salesHelper = useSalesHelper(productDescriptor);
  const isProductOnSale = salesHelper.isProductOnSale();
  const isPackageOnSale = salesHelper.isPackageOnSale(packCode);

  const disableWS = window.SCHA.config.disableAllWS;

  const showPrices = !disableWS && userHasPermission(Permission.PRICE);
  const stockVisible = userHasPermission(Permission.AVAILABILITY) && productDescriptor.base.quantityVisible;

  let atcButton;
  let atwButton;

  if (atcButtonVisible) {
    if (isProductPMA(productDescriptor)) {
      atcButton = (
        <OnRequestOnly className="scha__comparison-card__on-request-only" onClick={doAddToCart}>
          <InfoIcon className={"icon-margin"} sx={{ fontSize: 24 }} />
          <span>{t("product.pma.messages.short")}</span>
        </OnRequestOnly>
      );
    } else {
      atcButton = (
        <Button
          size={"medium"}
          startIcon={<ShoppingCartIcon  />}
          disabled={disableWS}
          onClick={doAddToCart}
          fullWidth
          sx={{ minWidth: "unset" }}
        >
          {userIsRegistered() ? t("addToCartPopup.add.submitBtn") : t("basket.addToBasket")}
        </Button>
      );
    }
  }

  if (wishlistButtonVisible) {
    if (atcButton) {
      atwButton = (
        <SideButton
          size={"small"}
          icon={<BookmarkIcon sx={{ fontSize: 24 }} />}
          color={"secondary"}
          onClick={onWishlistBtnClick}
          className="scha__comparison-page__add-to-wishlist"
          sx={{
            minWidth: "unset",
            width: 37,
            p: "0 !important",
            mr: { phone: "16px !important", tablet: "12px !important", "desktop-s": "16px !important" },
            flex: "none",
            "*": { margin: 0 },
            "> span > i": { p: "0 !important" },
          }}
        />
      );
    } else {
      atwButton = (
        <Button
          startIcon={<BookmarkIcon  />}
          onClick={onWishlistBtnClick}
          fullWidth
          sx={{ minWidth: "unset", "*": { margin: 0 } }}
        >
          {t("page.search.tabular.action.addToWishlist")}
        </Button>
      );
    }
  }

  return (
    <>
      {addToWishlistModal}
      <ProductDescriptorContext value={productDescriptor}>
        <ComparisonCardRoot className="scha__comparison-card">
          <IconButton className="close-icon" color={"primary"} onClick={handleCloseClick}>
            <CloseIcon  />
          </IconButton>

          {isProductOnSale && <FloatingSalesTag size={32} />}

          <Stack gap={2} sx={{ height: "100%" }}>
            <div className="product-image">
              <ProductImage product={productDescriptor.base} widths={WIDTHS} />
            </div>
            <Stack gap={0.5}>
              <Stack direction="row" alignItems="center">
                <Product.PreferredIndicator />
                <Product.PdpLink>
                  <Product.DesignationV2 variant="titleLarge" />
                </Product.PdpLink>
              </Stack>

              <Product.MMDTag />

              <Product.ShortDescriptionV2 variant="bodyLarge" />
              <Product.DescriptionV2 />
            </Stack>

            <NewVersionMessage
              className={"scha__comparison-card__new-version-msg"}
              baseProduct={productDescriptor.base}
            />

            <div className="old-designation-wrapper">
              <OldDesignationParameter label={t("productDetailPage.oldDesignation")} product={productDescriptor.base} />
            </div>

            {showPrices && (
              <>
                <PriceElements.Stack>
                  <Stack direction="row" gap={1} alignItems={"center"}>
                    <CurrentPrice
                      price={price}
                      zeroText={t("product.priceOnRequest")}
                      tariffSurcharge={tariffSurcharge}
                      variant="headlineMedium"
                      fontWeight={"bold"}
                      surchargeEnabled={hasSurcharge}
                    />
                    {isPackageOnSale && (
                      <SaleDiscountIndicator.V2
                        discountFormatted={discountFormatted}
                        variant="headlineSmall"
                        sx={{ fontWeight: 400 }}
                      />
                    )}
                  </Stack>
                  {oldPrice && <OldPrice price={oldPrice} />}
                  <PriceElements.NetPriceLabel price={netPrice} />
                </PriceElements.Stack>

                {showListPrice && listPrice && (
                  <Box>
                    <PriceElements.Price price={listPrice} />
                    <PriceElements.ListPriceLabel unit={netPrice?.unit ?? 1} />
                  </Box>
                )}
              </>
            )}

            {window.SCHA.feature.comparisonAvailability ? (
              <Product.StockIndicator />
            ) : (
              stockVisible && (
                <>
                  {productDescriptor.availability && (
                    <div className="stock" onClick={doAddToCart}>
                      <SimpleStockIndicator inStock={isInStock} checkAvailability />
                    </div>
                  )}
                  {productDescriptor.isLoading && <CircleSpinner className={"mt-3"} />}
                </>
              )
            )}

            <Stack direction="row" gap={1} sx={{ mt: "auto" }}>
              {atwButton}
              {atcButton}
            </Stack>
          </Stack>
        </ComparisonCardRoot>
      </ProductDescriptorContext>
    </>
  );
};

export default ProductComparisonCard;

const FloatingSalesTag = styled(SaleTag)({
  position: "absolute",
  top: 0,
  left: 0,
});

const ComparisonCardRoot = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(2),
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(0.5),
  width: "var(--card-width)",
  "--margin-size": "calc((var(--cell-width) - var(--card-width)) / 2)",
  marginLeft: "var(--margin-size)",
  marginRight: "var(--margin-size)",
  "&:last-child": {
    marginRight: 0,
  },
  "& .product-image": {
    marginTop: theme.spacing(0.625),
    [theme.breakpoints.down("desktop-s")]: {
      marginTop: theme.spacing(3.75),
    },
  },
  "& .title-wrapper": {
    lineHeight: "18px",
  },
  "& .title": {
    marginTop: theme.spacing(1.25),
    ...theme.typography.titleMedium,
  },
  "& .description, & .old-designation-wrapper": {
    marginTop: theme.spacing(1.25),
    ...theme.typography.titleSmall,
  },
  "& .price-on-request": {
    marginTop: theme.spacing(2.5),
    ...theme.typography.titleLarge,
    fontWeight: "bold",
  },
  "& .actions-wrapper": {
    display: "flex",
    flexDirection: "column",
    marginTop: "auto",
    "& .stock": {
      marginTop: theme.spacing(2.5),
      cursor: "pointer",
    },
    "& .actions": {
      display: "flex",
      marginTop: theme.spacing(2.5),
      height: theme.spacing(3.75),
    },
  },
  "& .close-icon": {
    position: "absolute",
    top: theme.spacing(0.625),
    right: theme.spacing(0.625),
  },
  "& .scha__comparison-card__new-version-msg": {
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(-1.25),
  },
}));

const OnRequestOnly = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  color: theme.palette.info.main,
  "& span": {
    textDecoration: "underline",
  },
}));

const CurrentPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  fontWeight: soldOut ? "normal" : "bold",
  color: soldOut ? theme.palette.text.secondary : theme.palette.text.primary,
  textDecoration: soldOut ? "line-through" : "none",
}));

const OldPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  color: soldOut ? theme.palette.text.primary : theme.palette.text.secondary,
  fontWeight: soldOut ? "bold" : "normal",
  textDecoration: soldOut ? "none" : "line-through",
}));
